import axios from 'axios'
import { apiConfig, authConfig, env } from '@/utils/env.js'
import { showWarning } from '@/utils/toast.js'
import { logger } from '@/utils/logger.js'

// Create axios instance with environment-based configuration
const apiClient = axios.create({
  baseURL: apiConfig.baseUrl,
  timeout: apiConfig.timeout,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

const pendingWorkflowActions = new Map()
const workflowActionPattern = /(^|\/)([^/]*-)?(submit|approve|reject)(-[^/]*)?(\/|$)/i

const normalizeRequestData = (data) => {
  if (!data) return ''
  if (typeof FormData !== 'undefined' && data instanceof FormData) return '[form-data]'
  if (typeof data === 'string') return data

  try {
    return JSON.stringify(data)
  } catch {
    return String(data)
  }
}

const getWorkflowActionKey = (config) => {
  const method = String(config.method || 'get').toLowerCase()
  const url = String(config.url || '')

  if (!['post', 'put', 'patch', 'delete'].includes(method) || !workflowActionPattern.test(url)) {
    return null
  }

  const params = config.params ? JSON.stringify(config.params) : ''
  return `${method}:${url}:${params}:${normalizeRequestData(config.data)}`
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from configured storage
    const storage = env.getStorage()
    const token = storage.getItem(authConfig.tokenKey)
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add CSRF token if enabled
    if (env.isFeatureEnabled('enableCsrf')) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken
      }
    }

    // Let Axios auto-set Content-Type with proper boundary for FormData
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    const requestId = logger.makeRequestId()
    config._requestId = requestId
    config._startedAt = performance.now()
    config.headers['X-Request-ID'] = requestId

    const workflowActionKey = getWorkflowActionKey(config)
    if (workflowActionKey) {
      if (pendingWorkflowActions.has(workflowActionKey)) {
        const error = new axios.CanceledError('Duplicate workflow action ignored')
        error.config = config
        error.isDuplicateWorkflowAction = true
        throw error
      }

      pendingWorkflowActions.set(workflowActionKey, true)
      config._workflowActionKey = workflowActionKey
    }
    
    // Log request in development
    logger.debug('API request', {
      requestId,
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL
    })
    
    return config
  },
  (error) => {
    logger.error('API request interceptor error', { error })
    return Promise.reject(error)
  }
)

// Response interceptor for global error handling
apiClient.interceptors.response.use(
  (response) => {
    if (response.config?._workflowActionKey) {
      pendingWorkflowActions.delete(response.config._workflowActionKey)
    }

    // Log successful response in development
    logger.debug('API response', {
      requestId: response.config?._requestId,
      status: response.status,
      url: response.config.url,
      durationMs: response.config?._startedAt ? Math.round(performance.now() - response.config._startedAt) : undefined
    })
    
    return response
  },
  (error) => {
    if (error.config?._workflowActionKey) {
      pendingWorkflowActions.delete(error.config._workflowActionKey)
    }

    if (error.isDuplicateWorkflowAction) {
      logger.warn('Duplicate workflow action ignored', {
        requestId: error.config?._requestId,
        url: error.config?.url
      })
      return Promise.reject(error)
    }

    const storage = env.getStorage()
    
    // Handle common errors
    if (error.response?.status === 401) {
      // Check if we're already trying to logout to prevent infinite loops
      const isLogoutRequest = error.config?.url?.includes('/auth/logout')
      
      // Unauthorized - clear auth data and redirect to login
      storage.removeItem(authConfig.tokenKey)
      storage.removeItem(authConfig.userDataKey)
      
      logger.warn('Unauthorized access - clearing auth data', {
        requestId: error.config?._requestId,
        url: error.config?.url
      })
      
      // Only dispatch the auth:logout event if this wasn't already a logout request
      if (!isLogoutRequest) {
        // Emit event for auth state change (can be caught by auth store/composable)
        window.dispatchEvent(new CustomEvent('auth:logout', { 
          detail: { reason: 'unauthorized', source: 'apiInterceptor' } 
        }))
      }
      
    } else if (error.response?.status === 403) {
      // Forbidden — show a warning toast unless the caller opted out with _silent: true.
      // Use _silent on requests where a 403 is expected and handled gracefully by the caller
      // (e.g. optional dropdowns that degrade when the user lacks permission).
      if (!error.config?._silent) {
        const backendMessage = error.response.data?.message
        const displayMessage = backendMessage || 'You do not have permission to perform this action.'
        logger.warn('Access forbidden', {
          requestId: error.config?._requestId,
          url: error.config?.url,
          message: displayMessage
        })
        showWarning(displayMessage, 'Access Denied')
      }

    } else if (error.response?.status === 422) {
      // Validation errors
      logger.debug('Validation errors', {
        requestId: error.config?._requestId,
        url: error.config?.url,
        errors: error.response.data?.errors
      })
      
    } else if (error.response?.status === 404) {
      // Not found
      logger.debug('Resource not found', {
        requestId: error.config?._requestId,
        url: error.config?.url,
        message: error.response?.data?.message
      })
      
    } else if (error.response?.status >= 500) {
      // Server errors
      logger.error('Server error', {
        requestId: error.config?._requestId,
        status: error.response?.status,
        url: error.config?.url,
        message: error.response?.data?.message,
        durationMs: error.config?._startedAt ? Math.round(performance.now() - error.config._startedAt) : undefined
      })
      
    } else if (error.code === 'ECONNABORTED') {
      // Timeout
      logger.error('Request timeout', {
        requestId: error.config?._requestId,
        url: error.config?.url
      })
      
    } else if (!error.response) {
      // Network error
      logger.error('Network error - no response received', {
        requestId: error.config?._requestId,
        url: error.config?.url,
        error
      })
    }
    
    return Promise.reject(error)
  }
)

/**
 * Identical GETs that are already in flight share one request.
 *
 * A page mounts several components that each ask for the same list, and the server was
 * answering the same question two or three times while the first answer was still on its
 * way. This is de-duplication, not caching: the entry is dropped the moment the request
 * settles, so the next call goes to the server as usual.
 *
 * Each caller after the first is handed its own copy of the data, so one component
 * mutating what it received cannot change what another sees.
 */
const inFlightGets = new Map()
const originalGet = apiClient.get.bind(apiClient)

const copyResponse = (response) => {
  try {
    return { ...response, data: structuredClone(response.data) }
  } catch {
    // Non-cloneable payloads (blobs, streams) are handed back as they are.
    return response
  }
}

apiClient.get = (url, config = {}) => {
  // Downloads, cancellable requests and anything asking to opt out go straight through.
  if (config.responseType === 'blob' || config.signal || config.skipDedupe) {
    return originalGet(url, config)
  }

  const key = `${url}|${JSON.stringify(config.params ?? {})}`
  const pending = inFlightGets.get(key)

  if (pending) {
    return pending.then(copyResponse)
  }

  const request = originalGet(url, config)
  inFlightGets.set(key, request)

  return request.finally(() => inFlightGets.delete(key))
}

export default apiClient
